import { supabaseServiceRole } from './supabase';
import { TelegramMessageData } from './telegram-types';
import { getDaysText } from '../pages/EquipmentPage';

interface SendTelegramNotificationParams {
  chatId: string;
  text: string;
  inlineKeyboard?: TelegramInlineKeyboard;
}

// Определение типов для встроенной клавиатуры Telegram
interface TelegramInlineKeyboardButton {
  text: string;
  callback_data: string;
}

export interface TelegramInlineKeyboard {
  inline_keyboard: TelegramInlineKeyboardButton[][];
}

/**
 * Отправляет сообщение в указанный чат Telegram через бота
 */
export const sendTelegramNotification = async (params: SendTelegramNotificationParams): Promise<{success: boolean, message_id?: number}> => {
  try {
    const { chatId, text, inlineKeyboard } = params;
    const botToken = import.meta.env.VITE_TELEGRAM_BOT_TOKEN || '6844879555:AAG79bDBiYwcMYKbAzDnL3nkq80bHqX1NnU';
    
    console.log(`Отправка уведомления в Telegram чат ${chatId}`);
    console.log(`Содержимое сообщения: ${text.substring(0, 50)}...`);
    
    // Проверяем формат ID чата
    if (!/^-?\d+$/.test(chatId)) {
      console.error(`Некорректный формат ID чата: ${chatId}, ожидается число или строка, начинающаяся с -`);
      return { success: false };
    }
    
    // Преобразуем ID чата в число, так как Telegram API требует числовое значение
    // Для групповых чатов ID начинается с -, поэтому корректно работаем с отрицательными числами
    const numericChatId = parseInt(chatId);
    
    if (isNaN(numericChatId)) {
      console.error(`Не удалось преобразовать ID чата в число: ${chatId}`);
      return { success: false };
    }
    
    // Формируем тело запроса с поддержкой встроенной клавиатуры
    const requestBody: Record<string, any> = {
      chat_id: numericChatId,
      text: text,
    };
    
    // Добавляем встроенную клавиатуру, если она указана
    if (inlineKeyboard) {
      requestBody.reply_markup = JSON.stringify(inlineKeyboard);
    }
    
    console.log('Тело запроса:', JSON.stringify(requestBody, null, 2));
    
    const apiUrl = `https://api.telegram.org/bot${botToken}/sendMessage`;
    console.log(`Отправка запроса к API Telegram: ${apiUrl}`);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при отправке сообщения в Telegram:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      return { success: false };
    }
    
    const result = await response.json();
    
    console.log('Сообщение успешно отправлено в Telegram:', result);
    
    // Возвращаем ID сообщения, если оно было успешно отправлено
    if (result.ok && result.result && result.result.message_id) {
      return { 
        success: true,
        message_id: result.result.message_id
      };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Ошибка при отправке уведомления в Telegram:', error);
    return { success: false };
  }
};

/**
 * Редактирует сообщение в Telegram
 */
export const editTelegramMessage = async (chatId: string, messageId: number, newText: string, inlineKeyboard?: TelegramInlineKeyboard): Promise<boolean> => {
  try {
    const botToken = import.meta.env.VITE_TELEGRAM_BOT_TOKEN || '6844879555:AAG79bDBiYwcMYKbAzDnL3nkq80bHqX1NnU';
    
    console.log(`Редактирование сообщения ${messageId} в чате ${chatId}`);
    console.log(`Новое содержимое: ${newText.substring(0, 50)}...`);
    
    // Проверяем формат ID чата
    if (!/^-?\d+$/.test(chatId)) {
      console.error(`Некорректный формат ID чата: ${chatId}, ожидается число или строка, начинающаяся с -`);
      return false;
    }
    
    // Преобразуем ID чата в число
    const numericChatId = parseInt(chatId);
    
    if (isNaN(numericChatId)) {
      console.error(`Не удалось преобразовать ID чата в число: ${chatId}`);
      return false;
    }
    
    // Формируем тело запроса
    const requestBody: Record<string, any> = {
      chat_id: numericChatId,
      message_id: messageId,
      text: newText
    };
    
    // Добавляем встроенную клавиатуру, если она указана
    if (inlineKeyboard) {
      requestBody.reply_markup = JSON.stringify(inlineKeyboard);
    }
    
    const apiUrl = `https://api.telegram.org/bot${botToken}/editMessageText`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при редактировании сообщения в Telegram:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      return false;
    }
    
    const result = await response.json();
    console.log('Результат редактирования сообщения в Telegram:', result);
    
    return result.ok === true;
  } catch (error) {
    console.error('Ошибка при редактировании сообщения в Telegram:', error);
    return false;
  }
};

/**
 * Удаляет сообщение из Telegram-чата
 */
export const deleteTelegramMessage = async (chatId: string, messageId: number): Promise<boolean> => {
  try {
    const botToken = import.meta.env.VITE_TELEGRAM_BOT_TOKEN || '6844879555:AAG79bDBiYwcMYKbAzDnL3nkq80bHqX1NnU';
    
    if (!messageId) {
      console.error('Не указан ID сообщения для удаления');
      return false;
    }
    
    console.log(`Удаление сообщения ${messageId} из чата ${chatId}`);
    
    // Проверяем формат ID чата
    if (!/^-?\d+$/.test(chatId)) {
      console.error(`Некорректный формат ID чата: ${chatId}, ожидается число или строка, начинающаяся с -`);
      return false;
    }
    
    // Преобразуем ID чата в число
    const numericChatId = parseInt(chatId);
    
    if (isNaN(numericChatId)) {
      console.error(`Не удалось преобразовать ID чата в число: ${chatId}`);
      return false;
    }
    
    const apiUrl = `https://api.telegram.org/bot${botToken}/deleteMessage`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: numericChatId,
        message_id: messageId
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при удалении сообщения из Telegram:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      return false;
    }
    
    const result = await response.json();
    console.log('Результат удаления сообщения из Telegram:', result);
    
    return result.ok === true;
  } catch (error) {
    console.error('Ошибка при удалении сообщения из Telegram:', error);
    return false;
  }
};

/**
 * Создает пустую клавиатуру для сообщений Telegram
 * ОБНОВЛЕНО: Возвращает пустую клавиатуру (без кнопок)
 */
const createBookingKeyboard = (): TelegramInlineKeyboard => {
  // Возвращаем пустую клавиатуру без кнопок
  return {
    inline_keyboard: []
  };
  
  // Раньше здесь был код для создания кнопок "Подтвердить", "Отменить" и т.д.
  // Теперь он отключен по требованию заказчика
};

/**
 * Форматирует и отправляет уведомление о новом бронировании в Telegram
 */
export const sendBookingNotification = async (bookingId: string): Promise<boolean> => {
  try {
    console.log(`Подготовка уведомления о бронировании ${bookingId}`);
    
    if (!bookingId) {
      console.error('Не передан ID бронирования для отправки уведомления');
      return false;
    }
    
    // Получаем данные о бронировании
    const { data: booking, error: bookingError } = await supabaseServiceRole
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();
      
    if (bookingError || !booking) {
      console.error('Ошибка при получении данных о бронировании:', bookingError, 'ID бронирования:', bookingId);
      return false;
    }
    
    console.log('Успешно получено бронирование:', booking);
    
    // Проверка user_id
    if (!booking.user_id) {
      console.error(`Бронирование ${bookingId} не содержит ID пользователя`);
      return false;
    }
    
    // Получаем данные пользователя
    const { data: userData, error: userError } = await supabaseServiceRole
      .from('users')
      .select('*')
      .eq('id', booking.user_id)
      .single();
      
    if (userError) {
      console.error('Ошибка при получении данных пользователя:', userError, 'ID пользователя:', booking.user_id);
    }
    
    console.log('Данные пользователя:', userData);
    
    // Получаем забронированное оборудование
    const { data: bookingEquipment, error: equipmentError } = await supabaseServiceRole
      .from('booking_equipment')
      .select('*, equipment(*)')
      .eq('booking_id', bookingId);
      
    if (equipmentError || !bookingEquipment) {
      console.error('Ошибка при получении данных об оборудовании:', equipmentError, 'ID бронирования:', bookingId);
      return false;
    }
    
    console.log('Успешно получено оборудование:', bookingEquipment);
    
    if (bookingEquipment.length === 0) {
      console.warn(`Не найдено оборудование для бронирования ${bookingId}`);
    }
    
    // Форматируем список оборудования
    let equipmentList = 'Оборудование не найдено';
    
    if (bookingEquipment && bookingEquipment.length > 0) {
      const equipmentItems = bookingEquipment.map(item => {
        try {
          const equipment = item.equipment;
          
          if (!equipment) {
            console.warn(`Отсутствуют данные об оборудовании для элемента ${item.id}`);
            return `• ID: ${item.equipment_id} - ${item.quantity} шт.`;
          }
          
          const days = Math.ceil((new Date(booking.end_date).getTime() - new Date(booking.start_date).getTime()) / (1000 * 60 * 60 * 24));
          const dailyRate = equipment.daily_rate || 0; // Защита от null
          const quantity = item.quantity || 1; // Защита от null
          const totalPrice = dailyRate * quantity * days;
          
          return `• ${equipment.name || 'Оборудование'} - ${quantity} шт. = ${totalPrice.toFixed(0)} руб`;
        } catch (error) {
          console.error('Ошибка при форматировании элемента оборудования:', error, 'Элемент:', item);
          return `• Ошибка данных оборудования`;
        }
      }).join('\n');
      
      if (equipmentItems) {
        equipmentList = equipmentItems;
      }
    }
    
    // Форматируем период бронирования с указанием количества дней
    const startDate = new Date(booking.start_date);
    const endDate = new Date(booking.end_date);
    const formattedStartDate = startDate.toLocaleDateString('ru-RU');
    const formattedEndDate = endDate.toLocaleDateString('ru-RU');
    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysText = getDaysText(days);
    const period = `${formattedStartDate} - ${formattedEndDate} (${days} ${daysText})`;
    
    // Получаем информацию о пользователе
    const userName = userData 
      ? `${userData.first_name || ''} ${userData.last_name || ''} ${userData.username ? `(@${userData.username})` : ''}` 
      : `ID пользователя: ${booking.user_id}`;

    // Добавляем статус бронирования
    const statusMap: Record<string, string> = {
      'pending': '⏳ Ожидает подтверждения',
      'confirmed': '✅ Подтверждено',
      'completed': '🏁 Завершено',
      'cancelled': '❌ Отменено'
    };
    
    const statusText = statusMap[booking.status as keyof typeof statusMap] || booking.status;
    
    // Собираем итоговое сообщение без ID бронирования, но с датой создания
    const bookingDate = new Date(booking.created_at || Date.now()).toLocaleDateString('ru-RU');
    const message = `🔔 Бронирование от ${bookingDate}

👤 Кто бронирует: ${userName}
${booking.project_name ? `📽 Проект: ${booking.project_name}` : ''}
📅 Период: ${period}
🚦 Статус: ${statusText}

📦 Забронированное оборудование:
${equipmentList}

💰 Сумма бронирования: 
${booking.total_price} руб

#броньТехники`;
    
    console.log('Подготовлено сообщение для отправки:', message);
    
    // ID чатов для отправки уведомлений
    const NOTIFICATION_CHAT_IDS = ['-1002515525241', '-1002484450880'];
    
    console.log(`Отправка уведомлений в чаты: ${NOTIFICATION_CHAT_IDS.join(', ')}`);
    
    // Создаем клавиатуру для сообщения
    const keyboard = createBookingKeyboard();
    
    // Флаг для отслеживания успешности отправки хотя бы в один чат
    let atLeastOneSent = false;
    
    // Отправляем сообщение в каждый телеграм чат
    for (const chatId of NOTIFICATION_CHAT_IDS) {
      try {
        const sendResult = await sendTelegramNotification({
          chatId,
          text: message,
          inlineKeyboard: keyboard
        });
        
        console.log(`Результат отправки уведомления в чат ${chatId}: ${sendResult.success ? 'успешно' : 'ошибка'}`);
        
        // Если сообщение успешно отправлено и получен его ID, сохраняем в базе данных
        if (sendResult.success && sendResult.message_id) {
          console.log(`Сохранение ID сообщения ${sendResult.message_id} для бронирования ${bookingId} в чате ${chatId}`);
          
          const numericChatId = parseInt(chatId);
          
          // Используем RPC вызов для добавления записи о сообщении
          const { error: insertError } = await supabaseServiceRole.rpc(
            'add_telegram_message',
            {
              p_booking_id: bookingId,
              p_chat_id: numericChatId,
              p_message_id: sendResult.message_id
            }
          );
            
          if (insertError) {
            console.error(`Ошибка при сохранении данных сообщения для чата ${chatId}:`, insertError);
          }
          
          atLeastOneSent = true;
        }
      } catch (error) {
        console.error(`Исключение при отправке уведомления в Telegram чат ${chatId}:`, error);
      }
    }
      
    return atLeastOneSent;
  } catch (error) {
    console.error('Ошибка при формировании и отправке уведомления о бронировании:', error);
    return false;
  }
};

/**
 * Удаляет сообщение из Telegram для указанного бронирования
 */
export const deleteBookingNotification = async (bookingId: string): Promise<boolean> => {
  try {
    if (!bookingId) {
      console.error('Не указан ID бронирования для удаления уведомления');
      return false;
    }
    
    console.log(`Удаление сообщений для бронирования ${bookingId}`);
    
    // Получаем все сообщения, связанные с бронированием
    const { data: allMessages, error: messagesError } = await supabaseServiceRole.rpc(
      'get_telegram_message_by_booking',
      {
        p_booking_id: bookingId
      }
    );
      
    if (messagesError) {
      console.error('Ошибка при получении данных сообщений:', messagesError);
      return false;
    }
    
    // Если сообщений нет, просто возвращаем true
    if (!allMessages || allMessages.length === 0) {
      console.log(`Сообщения для бронирования ${bookingId} не найдены`);
      return true;
    }
    
    console.log(`Найдено ${allMessages.length} сообщений для бронирования ${bookingId}:`, allMessages);
    
    // Преобразуем данные в более удобный формат
    const messagesData = allMessages.map((msg: TelegramMessageData) => ({
      chat_id: msg.chat_id.toString(),
      message_id: parseInt(msg.message_id.toString())
    }));
    
    let allDeleted = true;
    
    // Удаляем каждое найденное сообщение
    for (const message of messagesData) {
      try {
        console.log(`Удаление сообщения ${message.message_id} из чата ${message.chat_id}`);
        
        // Удаляем сообщение из Telegram
        const deleteResult = await deleteTelegramMessage(
          message.chat_id, 
          message.message_id
        );
        
        if (deleteResult) {
          console.log(`Сообщение ${message.message_id} успешно удалено из чата ${message.chat_id}`);
        } else {
          console.error(`Не удалось удалить сообщение ${message.message_id} из чата ${message.chat_id}`);
          allDeleted = false;
        }
      } catch (error) {
        console.error(`Ошибка при удалении сообщения ${message.message_id} из чата ${message.chat_id}:`, error);
        allDeleted = false;
      }
    }
    
    // Удаляем записи из базы данных (независимо от успеха удаления из Telegram)
    const { error: deleteError } = await supabaseServiceRole.rpc(
      'delete_telegram_message',
      {
        p_booking_id: bookingId
      }
    );
      
    if (deleteError) {
      console.error('Ошибка при удалении записей сообщений из базы данных:', deleteError);
    }
    
    return allDeleted;
  } catch (error) {
    console.error('Ошибка при удалении уведомления о бронировании из Telegram:', error);
    return false;
  }
};

/**
 * Редактирует сообщение в Telegram при изменении данных бронирования
 */
export const editBookingNotification = async (bookingId: string): Promise<boolean> => {
  try {
    if (!bookingId) {
      console.error('Не указан ID бронирования для редактирования уведомления');
      return false;
    }
    
    console.log(`Подготовка к редактированию уведомлений для бронирования ${bookingId}`);
    
    // Получаем данные о бронировании
    const { data: booking, error: bookingError } = await supabaseServiceRole
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();
      
    if (bookingError || !booking) {
      console.error('Ошибка при получении данных о бронировании:', bookingError, 'ID бронирования:', bookingId);
      return false;
    }
    
    // Получаем данные пользователя
    const { data: userData, error: userError } = await supabaseServiceRole
      .from('users')
      .select('*')
      .eq('id', booking.user_id)
      .single();
      
    if (userError) {
      console.error('Ошибка при получении данных пользователя:', userError, 'ID пользователя:', booking.user_id);
    }
    
    // Получаем забронированное оборудование
    const { data: bookingEquipment, error: equipmentError } = await supabaseServiceRole
      .from('booking_equipment')
      .select('*, equipment(*)')
      .eq('booking_id', bookingId);
      
    if (equipmentError || !bookingEquipment) {
      console.error('Ошибка при получении данных об оборудовании:', equipmentError, 'ID бронирования:', bookingId);
      return false;
    }
    
    // Форматируем список оборудования
    let equipmentList = 'Оборудование не найдено';
    
    if (bookingEquipment && bookingEquipment.length > 0) {
      const equipmentItems = bookingEquipment.map(item => {
        try {
          const equipment = item.equipment;
          
          if (!equipment) {
            return `• ID: ${item.equipment_id} - ${item.quantity} шт.`;
          }
          
          const days = Math.ceil((new Date(booking.end_date).getTime() - new Date(booking.start_date).getTime()) / (1000 * 60 * 60 * 24));
          const dailyRate = equipment.daily_rate || 0;
          const quantity = item.quantity || 1;
          const totalPrice = dailyRate * quantity * days;
          
          return `• ${equipment.name || 'Оборудование'} - ${quantity} шт. = ${totalPrice.toFixed(0)} руб`;
        } catch (error) {
          console.error('Ошибка при форматировании элемента оборудования:', error, 'Элемент:', item);
          return `• Ошибка данных оборудования`;
        }
      }).join('\n');
      
      if (equipmentItems) {
        equipmentList = equipmentItems;
      }
    }
    
    // Форматируем период бронирования с указанием количества дней
    const startDate = new Date(booking.start_date);
    const endDate = new Date(booking.end_date);
    const formattedStartDate = startDate.toLocaleDateString('ru-RU');
    const formattedEndDate = endDate.toLocaleDateString('ru-RU');
    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysText = getDaysText(days);
    const period = `${formattedStartDate} - ${formattedEndDate} (${days} ${daysText})`;
    
    // Получаем информацию о пользователе
    const userName = userData 
      ? `${userData.first_name || ''} ${userData.last_name || ''} ${userData.username ? `(@${userData.username})` : ''}` 
      : `ID пользователя: ${booking.user_id}`;

    // Добавляем статус бронирования
    const statusMap: Record<string, string> = {
      'pending': '⏳ Ожидает подтверждения',
      'confirmed': '✅ Подтверждено',
      'completed': '🏁 Завершено',
      'cancelled': '❌ Отменено'
    };
    
    const statusText = statusMap[booking.status as keyof typeof statusMap] || booking.status;
    
    // Собираем итоговое сообщение без ID бронирования, но с датой создания
    const bookingDate = new Date(booking.created_at || Date.now()).toLocaleDateString('ru-RU');
    const message = `🔔 Бронирование от ${bookingDate}

👤 Кто бронирует: ${userName}
${booking.project_name ? `📽 Проект: ${booking.project_name}` : ''}
📅 Период: ${period}
🚦 Статус: ${statusText}

📦 Забронированное оборудование:
${equipmentList}

💰 Сумма бронирования: 
${booking.total_price} руб

#броньТехники`;
    
    console.log('Подготовлено сообщение для обновления:', message);
    
    // Получаем все сообщения, связанные с бронированием
    const { data: allMessages, error: messagesError } = await supabaseServiceRole.rpc(
      'get_telegram_message_by_booking',
      {
        p_booking_id: bookingId
      }
    );
      
    if (messagesError) {
      console.error('Ошибка при получении данных сообщений для редактирования:', messagesError);
      // Не создаем новое сообщение при ошибке
      return false;
    }
    
    // Если сообщений нет, возвращаем false, не создаем новое
    if (!allMessages || allMessages.length === 0) {
      console.log(`Сообщения для редактирования бронирования ${bookingId} не найдены`);
      return false;
    }
    
    console.log(`Найдено ${allMessages.length} сообщений для редактирования бронирования ${bookingId}:`, allMessages);
    
    // Преобразуем данные в более удобный формат
    const messagesData = allMessages.map((msg: TelegramMessageData) => ({
      chat_id: msg.chat_id.toString(),
      message_id: parseInt(msg.message_id.toString())
    }));
    
    // Создаем клавиатуру для сообщения
    const keyboard = createBookingKeyboard();
    
    let atLeastOneUpdated = false;
    
    // Редактируем каждое найденное сообщение
    for (const message_info of messagesData) {
      try {
        console.log(`Редактирование сообщения ${message_info.message_id} в чате ${message_info.chat_id}`);
        
        // Редактируем сообщение
        const editResult = await editTelegramMessage(
          message_info.chat_id,
          message_info.message_id,
          message,
          keyboard
        );
        
        console.log(`Результат редактирования сообщения в чате ${message_info.chat_id}: ${editResult ? 'успешно' : 'ошибка'}`);
        
        if (editResult) {
          atLeastOneUpdated = true;
        }
      } catch (error) {
        console.error(`Ошибка при редактировании сообщения в чате ${message_info.chat_id}:`, error);
      }
    }
    
    // Возвращаем результат редактирования, не создаем новых сообщений даже если редактирование не удалось
    return atLeastOneUpdated;
  } catch (error) {
    console.error('Ошибка при редактировании уведомления о бронировании:', error);
    return false;
  }
};

/**
 * Тестовая функция для проверки отправки уведомлений
 */
export const testSendBookingNotification = async (chatId: string = '-1002515525241'): Promise<boolean> => {
  try {
    // Если пользователь указал конкретный чат - используем его, иначе отправляем в обе группы
    const targetChatIds = chatId ? [chatId] : ['-1002515525241', '-1002484450880'];
    
    for (const targetChatId of targetChatIds) {
      // Проверяем формат ID чата - должен быть числом или начинаться с -
      if (!/^-?\d+$/.test(targetChatId)) {
        console.error(`Неверный формат ID чата ${targetChatId}. ID чата должен быть числом или начинаться с - для групповых чатов`);
        continue;
      }
      
      console.log(`Отправка тестового уведомления в чат ${targetChatId}`);
      
      const testMessage = `🔔 Тестовое уведомление

👤 Кто бронирует: Тестовый Пользователь (@test_user)
📽 Проект: Тестовый проект
📅 Период: 01.06.2024 - 05.06.2024

📦 Забронированное оборудование:
• Камера Sony FX6 - 1 шт. (5000 руб/день) = 20000 руб
• Объектив Canon EF 24-70mm - 1 шт. (1500 руб/день) = 6000 руб
• Штатив Manfrotto - 1 шт. (500 руб/день) = 2000 руб

Сумма бронирования: 
28000 руб

#броньТехники #тест`;

      console.log('Сформировано тестовое сообщение');
      
      // Отправляем сообщение, используя числовое значение ID чата
      try {
        const result = await sendTelegramNotification({
          chatId: targetChatId,
          text: testMessage
        });
        
        console.log(`Результат отправки тестового уведомления в чат ${targetChatId}: ${result.success ? 'успешно' : 'ошибка'}`);
      } catch (sendError) {
        console.error(`Ошибка при отправке тестового уведомления в чат ${targetChatId}:`, sendError);
      }
    }
    
    return true; // Возвращаем true, если функция успешно выполнилась (даже если некоторые сообщения не отправились)
  } catch (error) {
    console.error('Ошибка при отправке тестового уведомления:', error);
    return false;
  }
}; 